import { ChevronLeft } from "lucide-react";

const sectionClass = "mb-8";
const headingClass = "font-montserrat font-bold text-xl text-white border-l-2 border-[#E0A458] pl-3 mb-3";
const cardClass = "rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 p-5 space-y-3";
const bodyClass = "text-slate-300 text-sm leading-relaxed";
const listClass = "list-disc list-inside space-y-1.5 text-slate-300 text-sm leading-relaxed";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-3xl mx-auto px-4 py-12">

        <a href="/" className="inline-flex items-center text-[#E0A458] hover:text-[#f0b467] text-sm mb-8 transition-colors">
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Home
        </a>

        <div className="rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-6 py-6 shadow-lg shadow-black/25 mb-8">
          <h1 className="font-montserrat font-bold text-3xl text-white">Privacy Policy</h1>
          <p className="text-slate-400 text-sm mt-1">Effective Date: July 23, 2026</p>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>1. Introduction</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              LegacyLoop ("we," "us," or "our") operates mylegacyloop.com and the Legacy Loop iOS app, and provides a private media vault and memory platform for youth sports families. This Privacy Policy describes how we collect, use, store, and share information when you use our services.
            </p>
            <p className={bodyClass}>
              By creating an account or using LegacyLoop, you agree to the practices described in this policy. If you do not agree, please do not use our services.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>2. Information We Collect</h2>
          <div className={cardClass}>
            <p className={bodyClass}><strong className="text-white">Account Information:</strong> When you register, we collect your name, email address, and password (hashed and managed by Supabase Auth). We never store plaintext passwords.</p>
            <p className={bodyClass}><strong className="text-white">Athlete Profile Data:</strong> Name, sport, position, graduation year, GPA, school, and biography that you enter for the athlete(s) on your account.</p>
            <p className={bodyClass}><strong className="text-white">Media Files:</strong> Photos, videos, and audio files you upload are stored on Amazon Web Services (AWS) S3. Files are accessed via short-lived presigned URLs and are not publicly accessible by default.</p>
            <p className={bodyClass}><strong className="text-white">Journal Entries:</strong> Written content you create, including any AI-enhanced versions generated at your request. Journal entries are stored in our database and are private to your account unless explicitly shared.</p>
            <p className={bodyClass}><strong className="text-white">Recruiting and College Data:</strong> Schools you add to the College Interest Tracker, interest levels, and notes you enter.</p>
            <p className={bodyClass}><strong className="text-white">Milestones and Achievements:</strong> Timeline events and achievement records you create for your athlete.</p>
            <p className={bodyClass}><strong className="text-white">Payment Information:</strong> Subscription billing is handled by Stripe. We do not store your card number, CVV, or full payment details. We retain only the Stripe customer ID, subscription status, and plan tier necessary to manage your account.</p>
            <p className={bodyClass}><strong className="text-white">Usage Data:</strong> Log data including pages visited, features used, device type, browser type, and IP address, used to operate and improve the service.</p>
            <p className={bodyClass}><strong className="text-white">Camera and Photo Library (iOS app):</strong> With your permission, the Legacy Loop app accesses your camera to capture new photos and videos, and your photo library so you can choose items to add to your vault. We access only the specific items you capture or select. The app does not scan, index, or upload your photo library in the background, and it requests these permissions in context, at the moment you use the feature. You can change or revoke this access anytime in iOS Settings.</p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>3. How We Use Your Information</h2>
          <div className={cardClass}>
            <ul className={listClass}>
              <li>Provide, operate, and maintain the LegacyLoop platform</li>
              <li>Authenticate your account and maintain session security</li>
              <li>Store and serve your uploaded media files securely</li>
              <li>Generate AI-assisted journal prompts and coach outreach emails at your request</li>
              <li>Process subscription payments and manage plan access</li>
              <li>Send transactional emails (account confirmations, subscription receipts, invitations)</li>
              <li>Respond to support requests submitted through our contact form</li>
              <li>Analyze usage patterns to improve features and fix bugs</li>
              <li>Comply with legal obligations</li>
            </ul>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>4. AI-Powered Features</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              LegacyLoop uses Anthropic Claude (an AI model) to power two features: AI-assisted journal prompts and the coach email writer in the Recruiting Center. When you use these features, relevant context (such as athlete sport, grade level, or your draft text) is sent to Anthropic's API to generate a response.
            </p>
            <p className={bodyClass}>
              We do not use your personal content to train AI models. Anthropic's data handling is governed by their own privacy policy. We recommend reviewing it at anthropic.com if you have questions about how they process API requests.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>5. Third-Party Services</h2>
          <div className={cardClass}>
            <p className={bodyClass}>We rely on the following third-party providers to operate LegacyLoop:</p>
            <ul className={listClass}>
              <li><strong className="text-white">Supabase</strong> -- Authentication, database hosting, and row-level security for user data</li>
              <li><strong className="text-white">Amazon Web Services (AWS) S3</strong> -- Secure cloud storage for uploaded photos, videos, and audio files</li>
              <li><strong className="text-white">Stripe</strong> -- Payment processing, subscription management, and billing</li>
              <li><strong className="text-white">Anthropic</strong> -- AI model API for journal prompts and coach email generation</li>
              <li><strong className="text-white">Resend</strong> -- Transactional email delivery for invitations, confirmations, and contact form submissions</li>
              <li><strong className="text-white">Vercel</strong> -- Frontend hosting and content delivery</li>
              <li><strong className="text-white">Render</strong> -- Backend API server hosting</li>
            </ul>
            <p className={bodyClass}>
              Each provider is subject to their own privacy and security policies. We choose providers that meet high standards for data security and, where applicable, are GDPR-compliant.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>6. How We Share Your Information</h2>
          <div className={cardClass}>
            <p className={bodyClass}>We do not sell your personal information. We share information only in the following circumstances:</p>
            <ul className={listClass}>
              <li><strong className="text-white">With your consent</strong> -- when you invite people to The Loop or share a Loop Link</li>
              <li><strong className="text-white">With service providers</strong> -- listed above, strictly to operate the platform</li>
              <li><strong className="text-white">For legal compliance</strong> -- when required by law, court order, or governmental authority</li>
              <li><strong className="text-white">To protect rights</strong> -- to investigate fraud, enforce our Terms of Service, or protect the safety of users</li>
              <li><strong className="text-white">In a business transfer</strong> -- if LegacyLoop is acquired or merges with another company, your data may transfer as part of that transaction, subject to the same privacy protections</li>
            </ul>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>7. The Loop and Shared Access</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              When you invite someone to The Loop, they gain access to the content you've specified at the access level you've assigned (View, Comment, or Admin). Vault content is never visible to members of The Loop under any access level.
            </p>
            <p className={bodyClass}>
              Loop Links are read-only shareable links that can be sent to anyone, including people without a LegacyLoop account. You control what is included in a Loop Link and can disable it at any time. Recipients of a Loop Link can view selected media but cannot download, comment, or modify any content.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>8. Children's Privacy (COPPA)</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              LegacyLoop, including the Legacy Loop iOS app, is directed to parents and legal guardians of youth athletes, not to children. Account holders must be at least 18 years old. Athlete profiles may include information about minors (such as a child's name, sport, and academic information), but this data is entered and controlled entirely by the parent or guardian who created the account.
            </p>
            <p className={bodyClass}>
              We do not knowingly collect personal information directly from children under the age of 13. If we become aware that a child under 13 has created an account without parental consent, we will delete that account and its associated data promptly.
            </p>
            <p className={bodyClass}>
              If you believe your child has provided personal information to us without your consent, please contact us at support@mylegacyloop.com so we can take appropriate action.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>9. Data Security</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              We implement industry-standard security measures to protect your data, including:
            </p>
            <ul className={listClass}>
              <li>HTTPS encryption for all data in transit</li>
              <li>Supabase row-level security to ensure users can only access their own data</li>
              <li>AWS S3 bucket policies that block public access; media is served only via short-lived presigned URLs</li>
              <li>JWT-based authentication tokens for all authenticated API requests</li>
              <li>Passwords are hashed and never stored in plaintext</li>
              <li>On the iOS app, your sign-in session is encrypted at rest on your device, with the encryption key stored in the iOS Keychain</li>
            </ul>
            <p className={bodyClass}>
              No method of transmission or storage is 100% secure. While we work hard to protect your information, we cannot guarantee absolute security.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>10. Data Retention</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              We retain your account data and uploaded content for as long as your account is active. Canceling your subscription does not delete your data; your account and content remain available to you until you choose to delete them. When you delete your account, your account and all associated data are permanently and immediately deleted, and this cannot be undone.
            </p>
            <p className={bodyClass}>
              Certain records (such as billing history) may be retained longer where required by law or to resolve disputes.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>11. Your Rights and Choices</h2>
          <div className={cardClass}>
            <ul className={listClass}>
              <li><strong className="text-white">Access and correction</strong> -- You can view and update your athlete profile and account information at any time from your Dashboard</li>
              <li><strong className="text-white">Media deletion</strong> -- You can delete individual media items or journal entries at any time from within the app</li>
              <li><strong className="text-white">Account deletion</strong> -- Permanently delete your account and all associated data at any time from the app (Settings, then Delete Account), or by contacting support@mylegacyloop.com</li>
              <li><strong className="text-white">Marketing emails</strong> -- You can unsubscribe from newsletter emails using the unsubscribe link in any email we send</li>
              <li><strong className="text-white">Data portability</strong> -- You can download your media files individually from the Media Library at any time</li>
            </ul>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>12. Cookies and Local Storage</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              LegacyLoop uses cookies and browser local storage to maintain your authentication session and remember your preferences. We do not use advertising cookies or third-party tracking cookies. The only cookies set are those necessary for the service to function. The Legacy Loop iOS app contains no advertising and no third-party analytics or tracking SDKs; we do not track you across other companies' apps or websites, and the app does not present an App Tracking Transparency prompt because we do not track you.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>13. Changes to This Policy</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              We may update this Privacy Policy from time to time. When we make material changes, we will update the effective date at the top of this page and, where appropriate, notify you by email. Continued use of the service after changes become effective constitutes your acceptance of the updated policy.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>14. Contact Us</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              If you have questions about this Privacy Policy or how we handle your data, please contact us:
            </p>
            <p className={bodyClass}>
              <strong className="text-white">Email:</strong> support@mylegacyloop.com<br />
              <strong className="text-white">Website:</strong> mylegacyloop.com/contact
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
